import { Fragment, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";
import { toast } from "sonner";

const EYEBROW = "font-eyebrow text-[11px] font-semibold uppercase tracking-[0.18em] sm:text-xs";

const GOLD = "#C9A227";
const GOLD_SOFT = "#D4AF37";
const CHOCOLATE = "#3D2817";
const MAROON = "#4A0E17";

const HOURS = Array.from({ length: 11 }, (_, i) => 10 + i);
const SLOT_MIN = 30;

const fmtTime = (d) => d.toTimeString().slice(0, 5);

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

const formatDateParts = (x) =>
    `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;

/** Local calendar date YYYY-MM-DD (avoids UTC shift from toISOString). */
const localDateInput = (d = new Date()) => {
    if (typeof d === "string" && DATE_RE.test(d)) return d;
    const x = d instanceof Date ? d : new Date(`${String(d).slice(0, 10)}T12:00:00`);
    if (Number.isNaN(x.getTime())) return formatDateParts(new Date());
    return formatDateParts(x);
};

/** Earliest bookable calendar date (today) for new appointments. */
const minBookingDate = () => formatDateParts(new Date());

const parseLocalDateTime = (dateStr, timeStr) => new Date(`${dateStr}T${timeStr}:00`);

const isValidBookingDate = (dateStr) => DATE_RE.test(dateStr || "");

/** Only today can be in the past — future dates are always allowed. */
const isBookingInPast = (dateStr, timeStr) => {
    if (!isValidBookingDate(dateStr) || !timeStr) return false;
    if (dateStr !== minBookingDate()) return false;
    const t = parseLocalDateTime(dateStr, timeStr);
    return Number.isNaN(t.getTime()) || t < new Date();
};

const isDateBeforeToday = (dateStr) => isValidBookingDate(dateStr) && dateStr < minBookingDate();

const formatTime12 = (timeStr) => {
    const [h, m] = timeStr.split(":").map(Number);
    if (Number.isNaN(h)) return timeStr;
    const suffix = h >= 12 ? "PM" : "AM";
    const hour12 = h % 12 || 12;
    return `${hour12}:${String(m || 0).padStart(2, "0")} ${suffix}`;
};

const SALON_OPEN = "10:00";
const SALON_LAST_SLOT = "19:30";

const parseTimeToMinutes = (timeStr) => {
    const [h, m] = timeStr.split(":").map(Number);
    return h * 60 + (m || 0);
};

const minutesToTime = (mins) =>
    `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;

/** Next 30-min grid slot at or after now, within salon hours. */
const earliestBookableTime = (dateStr) => {
    const openMins = parseTimeToMinutes(SALON_OPEN);
    const lastMins = parseTimeToMinutes(SALON_LAST_SLOT);
    const today = minBookingDate();

    let startMins = openMins;
    if (dateStr === today) {
        const now = new Date();
        const nowMins = now.getHours() * 60 + now.getMinutes();
        startMins = Math.max(openMins, Math.ceil((nowMins + 1) / SLOT_MIN) * SLOT_MIN);
    }

    if (startMins > lastMins) return null;
    return minutesToTime(startMins);
};

const defaultBookingTime = (dateStr) => earliestBookableTime(dateStr) || SALON_OPEN;

const clampBookingDate = (dateLike) => {
    const d = localDateInput(dateLike);
    const today = minBookingDate();
    return d < today ? today : d;
};

const STATUS_STYLES = {
    booked: { bg: "#FDE8EC", border: MAROON, text: MAROON },
    confirmed: { bg: "#E8EDFA", border: "#3B82C4", text: "#1E3A5F" },
    arrived: { bg: "#FDF3DC", border: GOLD, text: "#9A7B10" },
    "in-service": { bg: "#FBE4EF", border: "#D9468F", text: "#B83A72" },
    completed: { bg: "#FDF3DC", border: GOLD, text: MAROON },
    cancelled: { bg: "#F5E8EC", border: "#B8859C", text: "#8B5A6A" },
    "no-show": { bg: "#F5E8EC", border: "#B8859C", text: "#8B5A6A" },
};

const navBtn =
    "border-[#E8E4DE] bg-white text-[#3D2817] shadow-sm hover:border-[#D4AF37]/45 hover:bg-[#FFFEF9]";

function GoldDivider({ className = "max-w-[200px]" }) {
    return (
        <div className={`flex items-center ${className}`}>
            <div className="h-px flex-1" style={{ background: `linear-gradient(90deg, transparent, ${GOLD_SOFT}B3, transparent)` }} />
            <span className="px-2.5 text-[10px] leading-none" style={{ color: GOLD }}>◆</span>
            <div className="h-px flex-1" style={{ background: `linear-gradient(90deg, transparent, ${GOLD_SOFT}B3, transparent)` }} />
        </div>
    );
}

export default function CalendarPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [date, setDate] = useState(new Date());
    const [staff, setStaff] = useState([]);
    const [services, setServices] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [appts, setAppts] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);

    const dayStart = useMemo(() => {
        const d = new Date(date);
        d.setHours(0, 0, 0, 0);
        return d;
    }, [date]);

    const weekday = dayStart.toLocaleDateString("en-GB", { weekday: "long" });
    const dayMonth = dayStart.toLocaleDateString("en-GB", { day: "numeric", month: "long" });

    const load = async () => {
        const start = new Date(dayStart);
        const end = new Date(dayStart);
        end.setDate(end.getDate() + 1);
        const [a, s, sv, c] = await Promise.all([
            api.get("/appointments", {
                params: { date_from: start.toISOString(), date_to: end.toISOString() },
            }),
            api.get("/staff"),
            api.get("/services"),
            api.get("/customers"),
        ]);
        setAppts(a.data);
        setStaff(s.data);
        setServices(sv.data);
        setCustomers(c.data);
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dayStart.getTime()]);

    useEffect(() => {
        if (searchParams.get("book") !== "1") return;
        setEditing(null);
        setOpen(true);
        const next = new URLSearchParams(searchParams);
        next.delete("book");
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams]);

    return (
        <div className="space-y-6 p-6 lg:p-8" data-testid="calendar-page">
            <header className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
                <div>
                    <p className={EYEBROW} style={{ color: GOLD }}>
                        Schedule
                    </p>
                    <h1 className="mt-1.5 text-[1.375rem] font-bold leading-snug sm:text-[1.5rem]">
                        <span style={{ color: CHOCOLATE }}>{weekday}</span>{" "}
                        <span style={{ color: GOLD }}>{dayMonth}</span>
                    </h1>
                    <GoldDivider className="mt-3 max-w-[200px]" />
                    <p className="mt-3 flex items-center gap-2 text-sm font-semibold text-[#8A8178]">
                        <CalendarDays className="size-4 text-[#D4AF37]" />
                        {appts.length} appointment{appts.length !== 1 ? "s" : ""} today
                    </p>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    <Button size="icon" variant="outline" className={navBtn} onClick={() => {
                        const d = new Date(dayStart);
                        d.setDate(d.getDate() - 1);
                        setDate(d);
                    }} data-testid="cal-prev">
                        <ChevronLeft className="size-4" />
                    </Button>
                    <Button variant="outline" size="sm" className={navBtn} onClick={() => setDate(new Date())} data-testid="cal-today">
                        Today
                    </Button>
                    <Button size="icon" variant="outline" className={navBtn} onClick={() => {
                        const d = new Date(dayStart);
                        d.setDate(d.getDate() + 1);
                        setDate(d);
                    }} data-testid="cal-next">
                        <ChevronRight className="size-4" />
                    </Button>
                    <Input
                        type="date"
                        className="w-40 border-[#E8E4DE] bg-[#FFFEF9] text-sm font-medium text-[#3D2817] focus-visible:border-[#D4AF37]/55 focus-visible:ring-[#D4AF37]/20"
                        value={localDateInput(dayStart)}
                        onChange={(e) => setDate(new Date(e.target.value + "T12:00:00"))}
                        data-testid="cal-date-input"
                    />
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button
                                className="bg-[#4A0E17] font-semibold shadow-[0_4px_14px_rgba(74,14,23,0.25)] hover:bg-[#3a0b12]"
                                onClick={() => setEditing(null)}
                                data-testid="new-appointment-button"
                            >
                                <Plus className="mr-1 size-4" /> New booking
                            </Button>
                        </DialogTrigger>
                        <AppointmentDialog
                            key={editing?.id || `new-${localDateInput(dayStart)}`}
                            staff={staff}
                            services={services}
                            customers={customers}
                            initialDateStr={localDateInput(dayStart)}
                            editing={editing}
                            onClose={() => setOpen(false)}
                            onSaved={() => {
                                load();
                                setOpen(false);
                            }}
                        />
                    </Dialog>
                </div>
            </header>

            <div className="relative overflow-hidden rounded-xl border border-[#E8E4DE] bg-white shadow-[0_4px_22px_rgba(45,41,38,0.04)]">
                <div className="absolute inset-x-0 top-0 h-[2px] bg-[#4A0E17]/85" />

                {staff.length === 0 ? (
                    <div className="p-12 text-center">
                        <p className="font-serif text-xl font-semibold text-[#4A0E17]">No staff yet</p>
                        <p className="mt-2 text-sm font-medium text-[#8A8178]">Add staff first to see the calendar grid.</p>
                    </div>
                ) : (
                    <div className="scrollbar-salon overflow-x-auto pb-1">
                        <div
                            className="grid min-w-max"
                            style={{ gridTemplateColumns: `72px repeat(${staff.length}, minmax(168px, 1fr))` }}
                        >
                            <div className="sticky left-0 z-20 border-b border-r border-[#E8E4DE] bg-[#FFFEF9]" />
                            {staff.map((s) => (
                                <div
                                    key={s.id}
                                    className="flex items-center gap-2 border-b border-r border-[#E8E4DE] bg-gradient-to-b from-[#FFFEF9] to-white px-3 py-3"
                                >
                                    <span
                                        className="size-2.5 shrink-0 rounded-full ring-2 ring-white"
                                        style={{ background: s.color || MAROON }}
                                    />
                                    <span className="truncate text-sm font-bold text-[#3D2817]">{s.full_name}</span>
                                    <span className="ml-auto shrink-0 text-[10px] font-semibold uppercase tracking-wide text-[#8A8178]">
                                        {s.role}
                                    </span>
                                </div>
                            ))}

                            {HOURS.flatMap((h) =>
                                [0, 30].map((m) => (
                                    <Fragment key={`slot-${h}-${m}`}>
                                        <div
                                            className="sticky left-0 z-10 border-r border-[#E8E4DE] bg-[#FAF9F6]/90 px-2 py-2 text-[11px] font-semibold tabular-nums text-[#8A8178]"
                                            style={{ height: 60 }}
                                        >
                                            {m === 0 && `${String(h).padStart(2, "0")}:00`}
                                        </div>
                                        {staff.map((s) => {
                                            const matches = appts.filter((a) => {
                                                const start = new Date(a.start_at);
                                                return (
                                                    a.items.some((it) => it.staff_id === s.id) &&
                                                    start.getHours() === h &&
                                                    start.getMinutes() >= m &&
                                                    start.getMinutes() < m + 30
                                                );
                                            });
                                            return (
                                                <div
                                                    key={`c-${h}-${m}-${s.id}`}
                                                    className="relative border-b border-r border-dashed border-[#E8E4DE]/90 bg-white/60"
                                                    style={{ height: 60 }}
                                                >
                                                    {matches.map((a) => (
                                                        <ApptChip
                                                            key={a.id}
                                                            appt={a}
                                                            onClick={() => {
                                                                setEditing(a);
                                                                setOpen(true);
                                                            }}
                                                        />
                                                    ))}
                                                </div>
                                            );
                                        })}
                                    </Fragment>
                                ))
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function ApptChip({ appt, onClick }) {
    const start = new Date(appt.start_at);
    const end = new Date(appt.end_at);
    const totalMin = (end - start) / 60000;
    const offsetMin = start.getMinutes() % SLOT_MIN;
    const top = (offsetMin / SLOT_MIN) * 60;
    const height = (totalMin / SLOT_MIN) * 60 - 4;
    const style = STATUS_STYLES[appt.status] || STATUS_STYLES.booked;
    const svc = appt.items[0]?.service_name || "Service";
    const cancelled = appt.status === "cancelled" || appt.status === "no-show";

    return (
        <div
            onClick={onClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && onClick()}
            className={`absolute left-1 right-1 cursor-pointer overflow-hidden rounded-lg border px-2 py-1 shadow-sm transition-all hover:shadow-md hover:brightness-[0.98] ${cancelled ? "line-through opacity-80" : ""}`}
            style={{
                top: top + 2,
                height,
                backgroundColor: style.bg,
                borderColor: `${style.border}55`,
                borderLeftWidth: 3,
                borderLeftColor: style.border,
                color: style.text,
            }}
            data-testid={`appt-${appt.id}`}
        >
            <div className="truncate text-xs font-bold">{appt.customer.full_name}</div>
            <div className="truncate text-[10px] font-medium opacity-85">{svc}</div>
            <div className="text-[10px] font-semibold opacity-75">
                {fmtTime(start)} – {fmtTime(end)}
            </div>
        </div>
    );
}

function AppointmentDialog({ staff, services, customers, initialDateStr, editing, onClose, onSaved }) {
    const todayStr = minBookingDate();

    const seedDate = () => {
        if (editing) return localDateInput(new Date(editing.start_at));
        return clampBookingDate(initialDateStr);
    };

    const [customerId, setCustomerId] = useState("");
    const [serviceId, setServiceId] = useState("");
    const [staffId, setStaffId] = useState("");
    const [date, setDate] = useState(seedDate);
    const [time, setTime] = useState(() => (editing
        ? fmtTime(new Date(editing.start_at))
        : defaultBookingTime(seedDate())));
    const [notes, setNotes] = useState("");
    const [busy, setBusy] = useState(false);

    const editingPast = editing ? new Date(editing.start_at) < new Date() : false;
    const isToday = date === todayStr;
    const minTime = !editingPast && isToday ? (earliestBookableTime(date) || SALON_OPEN) : SALON_OPEN;
    const noSlotsToday = !editingPast && isToday && !earliestBookableTime(date);

    useEffect(() => {
        if (editing) {
            setCustomerId(editing.customer_id);
            setServiceId(editing.items[0]?.service_id || "");
            setStaffId(editing.items[0]?.staff_id || "");
            const d = new Date(editing.start_at);
            setDate(localDateInput(d));
            setTime(fmtTime(d));
            setNotes(editing.notes || "");
        } else {
            const initDate = clampBookingDate(initialDateStr);
            setCustomerId("");
            setServiceId("");
            setStaffId("");
            setDate(initDate);
            setTime(defaultBookingTime(initDate));
            setNotes("");
        }
    }, [editing, initialDateStr]);

    const handleDateChange = (newDate) => {
        if (!newDate || !isValidBookingDate(newDate)) return;
        if (!editingPast && isDateBeforeToday(newDate)) {
            setDate(todayStr);
            setTime(defaultBookingTime(todayStr));
            return;
        }
        setDate(newDate);
        if (!editingPast) {
            const next = defaultBookingTime(newDate);
            if (newDate === todayStr && !next) {
                toast.error("No slots left today");
                setTime(SALON_OPEN);
                return;
            }
            setTime(next || SALON_OPEN);
        }
    };

    const handleTimeChange = (newTime) => {
        if (!newTime) return;
        if (!editingPast && isToday && isBookingInPast(date, newTime)) {
            toast.error("Cannot select a past time");
            setTime(defaultBookingTime(date));
            return;
        }
        setTime(newTime);
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!customerId || !serviceId) {
            toast.error("Customer and service are required");
            return;
        }
        if (!isValidBookingDate(date)) {
            toast.error("Please select a date");
            return;
        }
        if (!editingPast && isBookingInPast(date, time)) {
            toast.error("Cannot book appointments in the past");
            return;
        }
        if (!editingPast && noSlotsToday) {
            toast.error("No slots available today");
            return;
        }
        setBusy(true);
        try {
            const start = parseLocalDateTime(date, time).toISOString();
            const payload = {
                customer_id: customerId,
                start_at: start,
                items: [{ service_id: serviceId, staff_id: staffId || null }],
                notes,
                source: "phone",
            };
            if (editing) {
                await api.put(`/appointments/${editing.id}`, payload);
                toast.success("Appointment updated");
            } else {
                await api.post("/appointments", payload);
                toast.success("Appointment created");
            }
            onSaved();
        } catch (err) {
            const detail = err.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Failed to save");
        } finally {
            setBusy(false);
        }
    };

    const fieldClass =
        "border-[#E8E4DE] bg-[#FFFEF9] focus-visible:border-[#D4AF37]/55 focus-visible:ring-[#D4AF37]/20";

    return (
        <DialogContent className="max-w-lg rounded-xl border-[#E8E4DE] p-6" data-testid="appointment-dialog">
            <DialogHeader>
                <DialogTitle className="font-serif text-2xl font-bold text-[#4A0E17]">
                    {editing ? "Edit appointment" : "New appointment"}
                </DialogTitle>
                <GoldDivider className="mt-2 max-w-[140px]" />
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4 pt-2">
                <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-wide text-[#3D2817]">Customer</Label>
                    <Select value={customerId} onValueChange={setCustomerId}>
                        <SelectTrigger className={fieldClass} data-testid="appt-customer">
                            <SelectValue placeholder="Select customer" />
                        </SelectTrigger>
                        <SelectContent>
                            {customers.map((c) => (
                                <SelectItem key={c.id} value={c.id}>
                                    {c.full_name} · {c.mobile}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-wide text-[#3D2817]">Service</Label>
                    <Select value={serviceId} onValueChange={setServiceId}>
                        <SelectTrigger className={fieldClass} data-testid="appt-service">
                            <SelectValue placeholder="Select service" />
                        </SelectTrigger>
                        <SelectContent>
                            {services.map((s) => (
                                <SelectItem key={s.id} value={s.id}>
                                    {s.name} · ₹{s.price} · {s.duration_min}m
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-wide text-[#3D2817]">Staff</Label>
                    <Select value={staffId} onValueChange={setStaffId}>
                        <SelectTrigger className={fieldClass} data-testid="appt-staff">
                            <SelectValue placeholder="Any staff" />
                        </SelectTrigger>
                        <SelectContent>
                            {staff.map((s) => (
                                <SelectItem key={s.id} value={s.id}>
                                    {s.full_name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                        <Label className="text-xs font-bold uppercase tracking-wide text-[#3D2817]">Date</Label>
                        <Input
                            type="date"
                            className={fieldClass}
                            value={date}
                            min={editingPast ? undefined : todayStr}
                            onChange={(e) => handleDateChange(e.target.value)}
                            disabled={editingPast}
                            data-testid="appt-date"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label className="text-xs font-bold uppercase tracking-wide text-[#3D2817]">Time</Label>
                        <Input
                            type="time"
                            className={fieldClass}
                            value={time}
                            min={editingPast ? undefined : minTime}
                            max={SALON_LAST_SLOT}
                            step={SLOT_MIN * 60}
                            onChange={(e) => handleTimeChange(e.target.value)}
                            onBlur={(e) => {
                                if (editingPast || !isToday || !e.target.value) return;
                                if (isBookingInPast(date, e.target.value)) {
                                    setTime(defaultBookingTime(date));
                                }
                            }}
                            disabled={editingPast || noSlotsToday}
                            data-testid="appt-time"
                        />
                    </div>
                </div>
                {editingPast && (
                    <p className="text-xs font-medium text-[#8A8178]">
                        Date and time cannot be changed for past appointments.
                    </p>
                )}
                {!editingPast && isToday && !noSlotsToday && (
                    <p className="text-xs font-medium text-[#8A8178]">
                        Today — from {formatTime12(minTime)} to {formatTime12(SALON_LAST_SLOT)}
                    </p>
                )}
                {noSlotsToday && (
                    <p className="text-xs font-medium text-[#C84B31]">
                        Salon is closed for today — pick a future date.
                    </p>
                )}
                <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-wide text-[#3D2817]">Notes</Label>
                    <Input className={fieldClass} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. allergic to ammonia" data-testid="appt-notes" />
                </div>
                <DialogFooter className="gap-2 pt-2">
                    <Button type="button" variant="outline" className={navBtn} onClick={onClose}>Cancel</Button>
                    <Button type="submit" disabled={busy || noSlotsToday} className="bg-[#4A0E17] font-semibold hover:bg-[#3a0b12]" data-testid="save-appointment-button">
                        {busy ? "Saving…" : "Save"}
                    </Button>
                </DialogFooter>
            </form>
        </DialogContent>
    );
}
